// Group payloads shared between server and SPA.
import { GroupRole } from './auth.js';
export const POST_PERMISSIONS = [
    'owner_only',
    'admins_only',
    'members_open',
];
/**
 * Display order for a picker: most permissive first, because `members_open` is
 * the default and the common answer. Deliberately not POST_PERMISSIONS, which is
 * ordered by privilege for validation.
 */
export const POST_PERMISSION_CHOICES = [
    'members_open',
    'admins_only',
    'owner_only',
];
/**
 * Label for a post permission, in the same spirit as groupRoleLabel().
 *
 * Lives here rather than in a view because it is asked for in two places — the
 * create-group modal and the Manage settings form — and those two had already
 * drifted apart once. The wording assumes the field is introduced by a question
 * like "Who can post photos & content?", so it does not repeat "can post".
 */
export function postPermissionLabel(permission) {
    switch (permission) {
        case 'owner_only':
            return 'Owner only';
        case 'admins_only':
            return 'Admins only';
        default:
            return 'Any member';
    }
}
export const INVITE_PERMISSIONS = [
    'owner_only',
    'admins_only',
    'members_open',
];
/** Picker order: least permissive first, because widening is the risky direction. */
export const INVITE_PERMISSION_CHOICES = [
    'owner_only',
    'admins_only',
    'members_open',
];
/** Assumes a question like "Who can invite new members?", so it does not repeat it. */
export function invitePermissionLabel(permission) {
    switch (permission) {
        case 'owner_only':
            return 'Owner only';
        case 'members_open':
            return 'Any member';
        default:
            return 'Owner and admins';
    }
}
/** The consequence, spelled out under each choice in the picker. */
export function invitePermissionHint(permission) {
    switch (permission) {
        case 'owner_only':
            return 'Only you can add people.';
        case 'members_open':
            return 'Anyone in the group can add someone. They can only add them as a Member.';
        default:
            return 'You and anyone you make an Admin can add people.';
    }
}
/**
 * May a caller with this group role invite, under this setting?
 *
 * ONE function, shared by the server's gate and the SPA's decision to render
 * the form at all. Two copies of this comparison would drift into a form that
 * shows and then 403s, or worse, a gate that is looser than the UI implies.
 */
export function canInvite(role, permission) {
    if (permission === 'members_open')
        return role >= GroupRole.MEMBER;
    if (permission === 'owner_only')
        return role >= GroupRole.OWNER;
    return role >= GroupRole.ADMIN;
}
/**
 * The highest role this inviter may hand the person they are inviting.
 *
 * **This is the load-bearing half of `members_open`.** Without it, letting any
 * member invite lets any member invite an ADMIN — a plain member minting
 * someone with power over the group, which is privilege escalation with extra
 * steps. A non-admin may only ever add a plain Member.
 */
export function maxInvitableRole(inviterRole) {
    return inviterRole >= GroupRole.ADMIN ? GroupRole.ADMIN : GroupRole.MEMBER;
}
/**
 * Roles an admin may actually grant. Owner (999) is deliberately absent: it is
 * reserved for the group creator and is transferred, never assigned. Rejecting
 * it is a server-side invariant, not just a UI omission.
 */
export const GRANTABLE_ROLES = [
    GroupRole.MEMBER,
    GroupRole.MODERATOR,
    GroupRole.EVENTS_MANAGER,
    GroupRole.ADMIN,
];
/**
 * Human label for a group role. Carried over verbatim, including the `>=`
 * comparisons, so an out-of-ladder value still lands on a sensible label.
 */
export function groupRoleLabel(role) {
    if (role >= GroupRole.OWNER)
        return 'Owner';
    if (role >= GroupRole.ADMIN)
        return 'Admin';
    if (role >= GroupRole.EVENTS_MANAGER)
        return 'Events Manager';
    if (role >= GroupRole.MODERATOR)
        return 'Moderator';
    return 'Member';
}
export const GROUP_VISIBILITIES = ['private', 'public'];
export function groupVisibilityLabel(v) {
    return v === 'public' ? 'Public: anyone signed in can find it' : 'Private: invitation only';
}
export const GROUP_JOIN_POLICIES = ['approval', 'instant'];
export function groupJoinPolicyLabel(p) {
    return p === 'instant'
        ? 'Anyone can join instantly'
        : 'An owner or admin approves each request';
}
/**
 * "11 Card Sharks", but never "1 Card Sharks".
 *
 * ⚠ `member_noun` stores ONE form and the UI needs TWO. That is the actual
 * defect behind a one-member group reading "1 members" — not a missing `+ 's'`
 * anywhere. There is no honest rule to derive a singular from an arbitrary
 * noun: "Card Sharks" → "Card Shark" works, and then a group picks `Family`,
 * `Crew`, `Staff` or `People` and every rule invented here is wrong. `members`
 * → `member` only ever looked derivable because it happens to be regular.
 *
 * So the singular SIDESTEPS the noun rather than guessing at it. At a count of
 * one the group's chosen word is dropped for the neutral "1 member", which is
 * always true and always grammatical; the flavour is given up in exactly the
 * one case where keeping it cannot be done correctly. Storing a second column
 * (`member_noun_one`) is the other honest answer and is what `sql/schema.sql`
 * points at if the singular is ever worth a migration.
 */
export function formatMemberCount(count, noun) {
    if (count === 1)
        return '1 member';
    return `${count} ${noun || 'members'}`;
}
/**
 * What the "Leave this group" confirmation says.
 *
 * ⚠ TWO THINGS A BARE "Leave <name>?" DID NOT SAY, and both surprise somebody
 * later rather than now.
 *
 * 1. **Notification settings are not kept.** `removeMember()` hard-deletes the
 *    group's `notification_prefs` row along with the membership, and every
 *    reader is `COALESCE(col, 1)` -- so absent means ON. A member who muted
 *    something deliberately, left, and came back is re-subscribed with nothing
 *    on screen saying so, and they will not connect it to having left months
 *    earlier. Saying it at the door is the cheap fix; keeping the row instead
 *    would leave prefs rows for non-members that every reader must then be
 *    checked against.
 * 2. **How hard it is to come back**, which is not the same answer for every
 *    group. A public group is rejoinable from "Find a group" in two taps. A
 *    private one needs a fresh invite from an owner or admin, so a mis-tap
 *    costs the person their access and costs an organizer an errand -- and the
 *    button sits directly under the member list, which is a place people tap to
 *    read rather than to act.
 *
 * Lives here rather than in the view for the reason `formatMemberCount` does:
 * it is a real branch with a real failure mode, and `web/` has no test runner.
 * The view keeps `window.confirm` -- native in the iOS shell, undismissable by
 * a stray click, and it sidesteps every `modal()` trap in CLAUDE.md for a
 * dialog whose whole content is one question.
 */
export function leaveGroupWarning(name, visibility) {
    const back = visibility === 'public'
        ? 'You can find it again under "Find a group".'
        : 'This group is private, so getting back in needs a new invite from an owner or admin.';
    return (`Leave ${name}?\n\n${back}\n\n` +
        'Your notification settings for this group are not kept. If you come back, ' +
        'they start again at the defaults.');
}
//# sourceMappingURL=groups.js.map